import { useEffect, useState } from 'react';
import Button from '@components/ui/Button';
import DatePicker from '@components/ui/DatePicker';

interface AIPurchaseDateRangeControlsProps {
    fromDate: string;
    toDate: string;
    onApply: (fromDate: string, toDate: string) => void;
}

/**
 * Compact date-range control for the AI Purchase Numbers pages, meant to sit inline
 * to the right of the page heading. Date picks only update local draft state — the
 * URL (and therefore the actual data refetch, which is keyed off the URL params in
 * `useAIPurchaseDateRangeParams`) only updates when "Apply" is clicked, mirroring the
 * local-state-then-button pattern used in `UnsyncedOrdersTab`/`SendEventTab`.
 * The draft re-syncs from the URL whenever `fromDate`/`toDate` change externally
 * (e.g. browser back/forward navigation between the 3 pages).
 */
export default function AIPurchaseDateRangeControls({
    fromDate,
    toDate,
    onApply,
}: AIPurchaseDateRangeControlsProps) {
    const [draftFromDate, setDraftFromDate] = useState(fromDate);
    const [draftToDate, setDraftToDate] = useState(toDate);

    useEffect(() => {
        setDraftFromDate(fromDate);
        setDraftToDate(toDate);
    }, [fromDate, toDate]);

    const isDirty = draftFromDate !== fromDate || draftToDate !== toDate;

    const handleApply = () => {
        onApply(draftFromDate, draftToDate);
    };

    return (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-shrink-0">
            <div className="w-full sm:w-40 md:w-48">
                <DatePicker
                    label="From"
                    value={draftFromDate}
                    onChange={(date) => date && setDraftFromDate(date.toISOString())}
                    maxDate={new Date()}
                />
            </div>
            <div className="w-full sm:w-40 md:w-48">
                <DatePicker
                    label="To"
                    value={draftToDate}
                    onChange={(date) => date && setDraftToDate(date.toISOString())}
                    maxDate={new Date()}
                />
            </div>
            <Button variant="primary" onClick={handleApply} disabled={!isDirty}>
                Apply
            </Button>
        </div>
    );
}
